// -----------------------------------------------------------------------
// <copyright file="OneButtonMessageBar.tsx" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import {
  IMessageBarStyles,
  IStyleFunctionOrObject,
  ITheme,
  MessageBar,
  MessageBarButton,
  MessageBarType,
  styled,
} from '@fluentui/react';
import React from 'react';
import { cherryHillButtonStyles, teamsSupportButtonStyles } from '../../self-help/styles/selfHelpStyles';
import { resolveStyles } from '../../utils/resolveStyles';
import { IOneButtonMessageBarStyleProps, getOneButtonMessageBarStyles } from './OneButtonMessageBar.styles';

export interface IOneButtonMessageBarProps {
  content: string;
  buttonText: string;
  messageBarType: MessageBarType;
  styles?: IStyleFunctionOrObject<IOneButtonMessageBarStyleProps, IMessageBarStyles>;
  theme?: ITheme;
  isTeams: boolean;
  onDismiss: () => void;
  onButtonClick: () => void;
}

const iconMap: Record<MessageBarType, string> = {
  [MessageBarType.info]: 'InfoSolid',
  [MessageBarType.warning]: 'WarningSolid',
  [MessageBarType.error]: 'ErrorSolid',
  [MessageBarType.blocked]: 'Blocked2',
  [MessageBarType.severeWarning]: 'Warning',
  [MessageBarType.success]: 'CompletedSolid',
};

const OneButtonMessageBarBase = (props: IOneButtonMessageBarProps): JSX.Element => {
  const styles = resolveStyles(
    {
      theme: props.theme,
      messageBarType: props.messageBarType,
      isTeams: props.isTeams,
    },
    props.styles
  );
  return (
    <MessageBar
      styles={styles}
      messageBarType={props.messageBarType}
      messageBarIconProps={{ iconName: iconMap[props.messageBarType] }}
      dismissIconProps={{ iconName: 'ChromeClose' }}
      dismissButtonAriaLabel={'Close'}
      onDismiss={props.onDismiss}
      actions={
        <MessageBarButton styles={props.isTeams ? teamsSupportButtonStyles : cherryHillButtonStyles} onClick={props.onButtonClick}>
          {props.buttonText}
        </MessageBarButton>
      }
    >
      {props.content}
    </MessageBar>
  );
};

export const OneButtonMessageBar = styled(OneButtonMessageBarBase, getOneButtonMessageBarStyles);
